import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { PROJECTS, CREATE_PROJECT, APPLY_TO_PROJECT } from '../graphql';
import { Plus, Users, Zap, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TYPES = ['HACKATHON', 'STARTUP', 'COLLEGE_PROJECT', 'RESEARCH', 'OPEN_SOURCE'];
const TYPE_COLORS = {
  HACKATHON: 'bg-orange-100 text-orange-700',
  STARTUP: 'bg-purple-100 text-purple-700',
  COLLEGE_PROJECT: 'bg-blue-100 text-blue-700',
  RESEARCH: 'bg-green-100 text-green-700',
  OPEN_SOURCE: 'bg-gray-100 text-gray-700',
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ type: '', skill: '' });
  const { data, loading } = useQuery(PROJECTS, { variables: {
    type: filters.type || null, skill: filters.skill || null
  }});
  const [createProject] = useMutation(CREATE_PROJECT, { refetchQueries: [PROJECTS] });
  const [applyToProject] = useMutation(APPLY_TO_PROJECT);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', type: 'HACKATHON', skillsNeeded: '', teamSize: 4 });
  const [applying, setApplying] = useState(null);
  const [message, setMessage] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    await createProject({ variables: {
      ...form,
      teamSize: parseInt(form.teamSize),
      skillsNeeded: form.skillsNeeded.split(',').map(s => s.trim()).filter(Boolean)
    }});
    setShowForm(false);
  };

  const handleApply = async (projectId) => {
    await applyToProject({ variables: { projectId, message } });
    setApplying(null);
    setMessage('');
    alert('Application sent!');
  };

  const projects = data?.projects || [];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Match</h1>
          <p className="text-gray-500 text-sm mt-1">Find teammates for hackathons, startups & projects</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Post Project
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input !pl-9 w-48" placeholder="Filter by skill..."
            value={filters.skill} onChange={e => setFilters({...filters, skill: e.target.value})} />
        </div>
        <select className="input w-48" value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})}>
          <option value="">All Types</option>
          {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card mb-6 space-y-4">
          <h3 className="font-semibold">Post a Project</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
              <input className="input" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select className="input" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea className="input !h-24 resize-none" required value={form.description}
                onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skills Needed (comma-separated)</label>
              <input className="input" value={form.skillsNeeded} onChange={e => setForm({...form, skillsNeeded: e.target.value})} placeholder="React, Node.js, ML" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Size</label>
              <input className="input" type="number" min="2" max="20" value={form.teamSize} onChange={e => setForm({...form, teamSize: e.target.value})} />
            </div>
          </div>
          <div className="flex gap-3">
            <button className="btn-primary" type="submit">Post Project</button>
            <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map(proj => (
            <div key={proj.id} className="card">
              <div className="flex items-start justify-between mb-2">
                <span className={`badge ${TYPE_COLORS[proj.type]}`}>{proj.type.replace('_', ' ')}</span>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Users size={14} /> {proj.currentMembers}/{proj.teamSize}
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{proj.title}</h3>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{proj.description}</p>

              <div className="flex flex-wrap gap-1 mb-3">
                {proj.skillsNeeded.map(s => (
                  <span key={s} className="badge bg-indigo-50 text-indigo-600">{s}</span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                    {proj.creator.name[0]}
                  </div>
                  <span className="text-xs text-gray-500">{proj.creator.name}</span>
                </div>
                {proj.creator.id !== user?.id && proj.currentMembers < proj.teamSize && (
                  <button className="btn-primary !py-1.5 !px-3 text-sm" onClick={() => setApplying(proj.id)}>
                    Apply
                  </button>
                )}
                {proj.currentMembers >= proj.teamSize && (
                  <span className="text-xs text-gray-400">Team full</span>
                )}
              </div>

              {applying === proj.id && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <textarea className="input !h-20 resize-none text-sm mb-2"
                    placeholder="Why do you want to join? (optional)"
                    value={message} onChange={e => setMessage(e.target.value)} />
                  <div className="flex gap-2">
                    <button className="btn-primary text-sm !py-1.5" onClick={() => handleApply(proj.id)}>Send Application</button>
                    <button className="btn-secondary text-sm !py-1.5" onClick={() => setApplying(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {projects.length === 0 && (
            <div className="col-span-2 text-center py-16 text-gray-400">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p>No projects posted yet. Be the first!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
